import {GridColumns} from '@mui/x-data-grid';
import {Box, Chip, IconButton, Stack, Typography} from '@mui/material';
import {FormattedMessage} from 'react-intl';
import {Delete} from '@mui/icons-material';
import {Link} from 'react-router-dom';
import {EditIcon} from 'src/components/icons/EditIcon';
import {ColoredStatus} from 'src/components/common/ColoredStatus';
import {CustomInvoiceItemDTO} from 'src/types';
import {CurrencyEnum} from 'src/enums';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {
    actionsButtonSx,
    actionsIconSx,
    actionsRootSx,
    descriptionCellSx,
    invoiceLinkSx,
    langLineSx,
    langPillSx,
    langTextSx,
    mutedDashSx,
    priceChipSx,
    qtyCellSx,
} from './CustomInvoiceItemsTable.sx';

export const formatPrice = (cents: number): string => (cents / 100).toFixed(2);

// Once an item is included in an invoice, it is locked (read-only).
export const isItemLocked = (row: CustomInvoiceItemDTO): boolean => !!row.invoice;

// French is mandatory and always highlighted as the primary line; English and
// German (optional) follow as muted secondary lines.
const PRIMARY_LANG = 'fr';

const LANGS: {code: string; lang: string; get: (row: CustomInvoiceItemDTO) => string | undefined}[] = [
    {code: 'FR', lang: 'fr', get: (row) => row.textFrench},
    {code: 'EN', lang: 'en', get: (row) => row.text},
    {code: 'DE', lang: 'de', get: (row) => row.textDeutsch},
];

const LangLine = ({code, text, primary}: {code: string; text: string; primary: boolean}) => (
    <Box sx={langLineSx}>
        <Box component="span" sx={langPillSx(primary)}>{code}</Box>
        <Typography component="span" sx={langTextSx(primary)}>{text}</Typography>
    </Box>
);

// French is always the highlighted primary line (floated to the top); the other
// filled translations follow as muted lines.
export const DescriptionCell = ({row}: {row: CustomInvoiceItemDTO}) => {
    const present = LANGS.map((l) => ({...l, value: l.get(row)})).filter(
        (l): l is typeof l & {value: string} => !!l.value
    );
    const ordered = [
        ...present.filter((l) => l.lang === PRIMARY_LANG),
        ...present.filter((l) => l.lang !== PRIMARY_LANG),
    ];

    return (
        <Box sx={descriptionCellSx}>
            {ordered.map((l) => (
                <LangLine key={l.code} code={l.code} text={l.value} primary={l.lang === PRIMARY_LANG}/>
            ))}
        </Box>
    );
};

export const QuantityCell = ({row}: {row: CustomInvoiceItemDTO}) => (
    <Typography component="span" sx={qtyCellSx}>
        {row.quantity}
    </Typography>
);

export const PriceCell = ({row}: {row: CustomInvoiceItemDTO}) => (
    <Chip
        label={`${formatPrice(row.price)} ${CurrencyEnum.CHF}`}
        size="small"
        sx={priceChipSx}
    />
);

export const StatusCell = ({row}: {row: CustomInvoiceItemDTO}) =>
    row.status ? (
        <ColoredStatus label={row.status}/>
    ) : (
        <Typography component="span" sx={mutedDashSx}>—</Typography>
    );

export const InvoiceCell = ({row}: {row: CustomInvoiceItemDTO}) =>
    row.invoice ? (
        <Box
            component={Link}
            to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.invoices, String(row.invoice.id)])}
            sx={invoiceLinkSx}
        >
            {row.invoice.invoiceNumber}
        </Box>
    ) : (
        <Typography component="span" sx={mutedDashSx}>—</Typography>
    );

export const columnConfigCustomInvoiceItems = (
    // Undefined means "not permitted", so the control is not drawn at all. A disabled one would say
    // the action exists and is merely unavailable, which is a different and wrong claim.
    onEdit?: (item: CustomInvoiceItemDTO) => void,
    onDelete?: (id: number) => void
): GridColumns => [
    {
        field: 'text',
        renderHeader: () => <FormattedMessage id="description"/>,
        flex: 2,
        minWidth: 220,
        sortable: false,
        cellClassName: 'ampTableCell',
        renderCell: (params) => (
            <DescriptionCell row={params.row as CustomInvoiceItemDTO}/>
        ),
    },
    {
        field: 'quantity',
        renderHeader: () => <FormattedMessage id="quantity"/>,
        width: 80,
        sortable: false,
        cellClassName: 'ampTableCell',
        align: 'center' as const,
        headerAlign: 'center' as const,
        renderCell: (params) => (
            <QuantityCell row={params.row as CustomInvoiceItemDTO}/>
        ),
    },
    {
        field: 'price',
        renderHeader: () => <FormattedMessage id="priceInclVat"/>,
        width: 170,
        sortable: false,
        cellClassName: 'ampTableCell',
        renderCell: (params) => (
            <PriceCell row={params.row as CustomInvoiceItemDTO}/>
        ),
    },
    {
        field: 'status',
        renderHeader: () => <FormattedMessage id="status"/>,
        width: 130,
        sortable: false,
        cellClassName: 'ampTableCell',
        renderCell: (params) => <StatusCell row={params.row as CustomInvoiceItemDTO}/>,
    },
    {
        field: 'invoice',
        renderHeader: () => <FormattedMessage id="invoice"/>,
        width: 130,
        sortable: false,
        cellClassName: 'ampTableCell',
        renderCell: (params) => <InvoiceCell row={params.row as CustomInvoiceItemDTO}/>,
    },
    {
        field: 'actions',
        renderHeader: () => '',
        width: 96,
        sortable: false,
        cellClassName: 'ampTableCell',
        align: 'right' as const,
        headerAlign: 'right' as const,
        renderCell: (params) => {
            const row = params.row as CustomInvoiceItemDTO;
            const locked = isItemLocked(row);
            return (
                <Stack sx={actionsRootSx}>
                    {onEdit && (
                        <IconButton sx={actionsButtonSx} disabled={locked} onClick={() => onEdit(row)}>
                            <EditIcon/>
                        </IconButton>
                    )}
                    {onDelete && (
                        <IconButton sx={actionsButtonSx} disabled={locked} onClick={() => onDelete(row.id)}>
                            <Delete sx={actionsIconSx}/>
                        </IconButton>
                    )}
                </Stack>
            );
        },
    },
];
